import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";

import { LockedSlots_atom } from "@/features/LockSlots/atoms";
import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  CreateRequestBookingInput,
  useCreateNewRequestBookingMutation,
} from "./__generated__/useCreateNewRequestBookingMutation.graphql";

export const useCreateNewRequestBooking = () => {
  const { showToast } = useToast();
  const updateLockSlotInfo = useSetAtom(LockedSlots_atom);

  const [createRequestBookingMutation, isCreating] =
    useMutationWithError<useCreateNewRequestBookingMutation>(
      createRequestBookingMutationString
    );

  const handleCreateRequestBooking = (
    input: CreateRequestBookingInput,
    successCallback?: (bookingId: string) => void
  ) => {
    createRequestBookingMutation({
      variables: { input },
      onCompleted: (response) => {
        const data = response.responsePayload;
        if (data?.success) {
          updateLockSlotInfo(null);
          // Get the first booking ID if available
          const bookingId = data.booking?.id;
          if (bookingId && successCallback) {
            successCallback(bookingId);
          }
        }
      },
      onCompletedErrorCustom: (errors) => {
        showToast({
          description: t`Sorry. An error occurred while submitting your request.`,
          semanticStyle: "error",
          toastId: "create-request-booking-error",
        });
        return [];
      },
    });
  };

  return [handleCreateRequestBooking, isCreating] as const;
};

const createRequestBookingMutationString = graphql`
  mutation useCreateNewRequestBookingMutation(
    $input: CreateRequestBookingInput!
  ) {
    responsePayload: createRequestBooking(input: $input) {
      errors {
        field
        code
        message
      }
      success
      booking {
        id
      }
    }
  }
`;
